'use client'
import React, { FC } from 'react'
import { Container, Flex, Text } from '@chakra-ui/react'
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api'
import type { Library } from '@googlemaps/js-api-loader'
import { mapStyle } from '@/app/components/common/CustomMap/map-style'
import { RealEstateDTO } from '@/core/real-estate/typings'
import { API_KEY } from '@/constants'
import { useLocaleConfig } from '@/hooks/use-locale-config'

interface RealEstateDetailMapProps {
  location: RealEstateDTO['location']
}

const GOOGLE_MAP_LIBRARIES: Library[] = ['geometry', 'places']

/**
 * Renders the public property detail map.
 *
 * @param location - Property coordinates for the map center and marker.
 * @returns The Google Maps section for the property detail page.
 */
const RealEstateDetailMap: FC<RealEstateDetailMapProps> = ({ location }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: API_KEY!,
    libraries: GOOGLE_MAP_LIBRARIES
  })

  const config = useLocaleConfig()

  if (!isLoaded) {
    return null
  }

  if (loadError) {
    return (
      <Flex alignItems="center" justifyContent="center" w="100vw" h="100vh">
        <Text color="red.400">Error al cargar el mapa: {loadError.message}</Text>
      </Flex>
    )
  }

  return (
    <Container
      py={10}
      h={{ base: '90vw', md: '60vh' }}
      id="realestate-detail-map"
      maxW="container.xl">
      <GoogleMap
        id="rea-estate-detail-map-component"
        mapContainerStyle={{
          height: '100%',
          width: '100%'
        }}
        zoom={15}
        center={{
          lng: location.coordinates[0] || config.coordinates.lng,
          lat: location.coordinates[1] || config.coordinates.lat
        }}
        options={{
          styles: mapStyle
        }}>
        <Marker
          position={{
            lng: location.coordinates[0] || config.coordinates.lng,
            lat: location.coordinates[1] || config.coordinates.lat
          }}
        />
      </GoogleMap>
    </Container>
  )
}

export default RealEstateDetailMap
